// Thunder Spirit Effect
// A high-fidelity, ultra-premium animation for the "Thunder Sprint/Spirit" card.
// Narrative: The caster summons a massive electrical spirit that strikes them, dealing damage but overcharging their power.
// Breakdown:
// 1. Gathering (0 - 800ms): Screen darkens, particles rush toward caster.
// 2. The Strike (800 - 1000ms): Massive multi-branch lightning bolt strikes the caster.
// 3. Crawler Lightning (1000 - 3000ms): "Back to the Future" style electricity crawling all over the avatar.
// 4. Overcharge (3000 - 4500ms): The static coalesces into 5 orbiting energy orbs (representing +5 prep slots).
// 5. Dissipation (4500 - 5000ms): Smoke and sparks fade out.

const CONFIG = {
    duration: 5000,
    strikeTime: 800,
    crawlTime: 1000,
    shatterTime: 3000,
    numOrbs: 5,
};

// --- Rendering Utilities & Caches ---

// Spark Particle
class Spark {
    x: number; y: number; vx: number; vy: number; life: number; maxLife: number; color: string; size: number;
    constructor(x: number, y: number, color: string, speedMulti: number = 1) {
        this.x = x; this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = (2 + Math.random() * 8) * speedMulti;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.maxLife = 30 + Math.random() * 40;
        this.life = this.maxLife;
        this.color = color;
        this.size = 1 + Math.random() * 3;
    }
    update(gx: number, gy: number) {
        this.x += this.vx; this.y += this.vy;
        this.vx += gx; this.vy += gy;
        this.vx *= 0.95; this.vy *= 0.95;
        this.life--;
    }
    draw(ctx: CanvasRenderingContext2D, alpha: number) {
        if (this.life <= 0) return;
        ctx.globalAlpha = (this.life / this.maxLife) * alpha;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
    }
}

// Orb for Overcharge phase
class EnergyOrb {
    angle: number; radius: number; speed: number; phase: 'orbit' | 'absorb';
    x: number; y: number; trail: { x: number, y: number }[] = [];
    constructor(angleOffset: number) {
        this.angle = angleOffset;
        this.radius = 150;
        this.speed = 0.1;
        this.phase = 'orbit';
        this.x = 0; this.y = 0;
    }
    update(cx: number, cy: number, dt: number) {
        this.angle += this.speed * dt * 0.05;
        if (this.phase === 'absorb') {
            this.radius -= 5 * dt * 0.05;
            this.speed += 0.02 * dt * 0.05;
        } else {
            // Wobble radius
            this.radius = 150 + Math.sin(this.angle * 3) * 20;
        }
        this.x = cx + Math.cos(this.angle) * this.radius;
        this.y = cy + Math.sin(this.angle) * this.radius * 0.6; // Elliptical

        this.trail.unshift({ x: this.x, y: this.y });
        if (this.trail.length > 15) this.trail.pop();
    }
    draw(ctx: CanvasRenderingContext2D, alpha: number) {
        if (this.radius < 10) return; // Absorbed

        ctx.globalAlpha = alpha;
        ctx.lineWidth = 4;
        ctx.strokeStyle = `rgba(180, 240, 255, ${alpha})`;
        ctx.beginPath();
        for (let i = 0; i < this.trail.length; i++) {
            const p = this.trail[i];
            if (i === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();

        ctx.shadowBlur = 20;
        ctx.shadowColor = '#00ffff';
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(this.x, this.y, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
    }
}

// --- Lightning Generation ---
type LineSegment = { x1: number, y1: number, x2: number, y2: number, opacity: number, width: number };

function generateLightning(x1: number, y1: number, x2: number, y2: number, displace: number, iterations: number = 5): LineSegment[] {
    let segments: LineSegment[] = [{ x1, y1, x2, y2, opacity: 1, width: 6 }];

    for (let i = 0; i < iterations; i++) {
        const nextSegments: LineSegment[] = [];
        segments.forEach(seg => {
            const midX = (seg.x1 + seg.x2) / 2;
            const midY = (seg.y1 + seg.y2) / 2;

            const dx = seg.x2 - seg.x1;
            const dy = seg.y2 - seg.y1;
            const len = Math.sqrt(dx * dx + dy * dy);
            const normalX = -dy / len;
            const normalY = dx / len;

            const shiftX = normalX * (Math.random() - 0.5) * displace;
            const shiftY = normalY * (Math.random() - 0.5) * displace;

            nextSegments.push({ x1: seg.x1, y1: seg.y1, x2: midX + shiftX, y2: midY + shiftY, opacity: seg.opacity, width: seg.width });
            nextSegments.push({ x1: midX + shiftX, y1: midY + shiftY, x2: seg.x2, y2: seg.y2, opacity: seg.opacity, width: seg.width });

            // Branching
            if (Math.random() < 0.2 && i < iterations - 1) {
                const angleOffset = (Math.random() - 0.5) * 1.5;
                const branchLen = len * 0.6 * Math.random();
                const bx = midX + shiftX + Math.cos(Math.atan2(dy, dx) + angleOffset) * branchLen;
                const by = midY + shiftY + Math.sin(Math.atan2(dy, dx) + angleOffset) * branchLen;
                nextSegments.push({ x1: midX + shiftX, y1: midY + shiftY, x2: bx, y2: by, opacity: seg.opacity * 0.6, width: seg.width * 0.6 });
            }
        });
        segments = nextSegments;
        displace *= 0.5;
    }
    return segments;
}

// Crawler Lightning for Back to the Future effect
class CrawlLightning {
    segments: LineSegment[];
    life: number;
    maxLife: number;
    constructor(cx: number, cy: number, radius: number) {
        // Random start and end on the perimeter or slightly inside
        const a1 = Math.random() * Math.PI * 2;
        const a2 = a1 + (Math.random() - 0.5) * Math.PI; // Nearby end point
        const r1 = radius * (0.6 + Math.random() * 0.6); // 0.6x to 1.2x radius
        const r2 = radius * (0.6 + Math.random() * 0.6);

        const x1 = cx + Math.cos(a1) * r1;
        const y1 = cy + Math.sin(a1) * r1;
        const x2 = cx + Math.cos(a2) * r2;
        const y2 = cy + Math.sin(a2) * r2;

        // Use only 3 iterations for smaller, jagged arcs
        this.segments = generateLightning(x1, y1, x2, y2, 20, 3);
        this.maxLife = 50 + Math.random() * 150; // milliseconds
        this.life = this.maxLife;
    }

    update(dt: number) {
        this.life -= dt;
    }

    draw(ctx: CanvasRenderingContext2D, alpha: number) {
        if (this.life <= 0) return;
        const progress = this.life / this.maxLife; // 1 to 0
        const flicker = Math.random() > 0.1 ? 1 : 0.2;
        const intensity = progress * flicker * alpha;

        this.segments.forEach(seg => {
            ctx.beginPath();
            ctx.moveTo(seg.x1, seg.y1);
            ctx.lineTo(seg.x2, seg.y2);

            ctx.lineWidth = (seg.width * 0.4) + Math.random() * 2;
            ctx.strokeStyle = `rgba(150, 255, 255, ${seg.opacity * intensity})`;
            ctx.shadowBlur = 10;
            ctx.shadowColor = '#00ffff';
            ctx.stroke();

            ctx.lineWidth = seg.width * 0.2;
            ctx.strokeStyle = `rgba(255, 255, 255, ${seg.opacity * intensity})`;
            ctx.shadowBlur = 0;
            ctx.stroke();
        });
    }
}


// --- Effect State ---
export type ThunderSpiritEffect = {
    kind: 'thunderSpirit';
    t: number;
    tx: number; // Target X (Caster)
    ty: number; // Target Y (Caster)
    data?: {
        sparks: Spark[];
        orbs: EnergyOrb[];
        crawlers: CrawlLightning[];
        lightningBolt: LineSegment[] | null;
        lastT: number;
    }
}

export const createThunderSpiritEffect = (tx: number, ty: number): ThunderSpiritEffect => ({
    kind: 'thunderSpirit', t: 0, tx, ty
});

export const renderThunderSpiritEffect = (
    eff: ThunderSpiritEffect,
    ctx: CanvasRenderingContext2D
): boolean => {

    if (eff.t >= CONFIG.duration) return false;

    if (!eff.data) {
        eff.data = {
            sparks: [],
            orbs: Array.from({ length: CONFIG.numOrbs }, (_, i) => new EnergyOrb((i / CONFIG.numOrbs) * Math.PI * 2)),
            crawlers: [],
            lightningBolt: null,
            lastT: Date.now()
        };
    }

    const { width, height } = ctx.canvas;
    const dt = Date.now() - eff.data.lastT;
    eff.data.lastT = Date.now();
    const d = eff.data;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // 1. Gathering & Screen Darken
    let darkenAlpha = 0;
    if (eff.t < CONFIG.strikeTime) {
        darkenAlpha = (eff.t / CONFIG.strikeTime) * 0.8;
    } else if (eff.t < CONFIG.shatterTime) {
        darkenAlpha = 0.8;
    } else {
        darkenAlpha = 0.8 * (1 - (eff.t - CONFIG.shatterTime) / (CONFIG.duration - CONFIG.shatterTime));
    }

    if (darkenAlpha > 0.01) {
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = `rgba(10, 5, 20, ${darkenAlpha})`;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'lighter';
    }

    // Sky gathering particles
    if (eff.t < CONFIG.strikeTime) {
        if (Math.random() < 0.5) {
            const sx = Math.random() * width;
            const sy = Math.random() * height * 0.5;
            d.sparks.push(new Spark(sx, sy, '#00ffff', 0.5));
            // Pull them towards center top
            const s = d.sparks[d.sparks.length - 1];
            const dx = eff.tx - s.x;
            const dy = -100 - s.y; // High in the sky above caster
            const dist = Math.sqrt(dx * dx + dy * dy);
            s.vx = (dx / dist) * 15;
            s.vy = (dy / dist) * 15;
        }
    }

    // 2. The Strike
    if (eff.t >= CONFIG.strikeTime && eff.t <= CONFIG.crawlTime) {
        const strikeProgress = (eff.t - CONFIG.strikeTime) / (CONFIG.crawlTime - CONFIG.strikeTime);

        // Generate new lightning every few frames for a flickering effect
        if (!d.lightningBolt || Math.random() < 0.3) {
            d.lightningBolt = generateLightning(eff.tx, -200, eff.tx, eff.ty, 150);
        }

        const flashIntensity = 1 - Math.pow(strikeProgress, 2);

        // Render flash
        if (flashIntensity > 0.1) {
            ctx.fillStyle = `rgba(200, 255, 255, ${flashIntensity * 0.6})`;
            ctx.fillRect(0, 0, width, height);
        }

        // Render main bolt
        d.lightningBolt.forEach(seg => {
            ctx.beginPath();
            ctx.moveTo(seg.x1, seg.y1);
            ctx.lineTo(seg.x2, seg.y2);
            ctx.lineWidth = seg.width + Math.random() * 4;
            ctx.strokeStyle = `rgba(150, 255, 255, ${seg.opacity * flashIntensity})`;
            ctx.shadowBlur = 15;
            ctx.shadowColor = '#00ffff';
            ctx.stroke();

            // Core
            ctx.lineWidth = seg.width * 0.4;
            ctx.strokeStyle = `rgba(255, 255, 255, ${seg.opacity * flashIntensity})`;
            ctx.shadowBlur = 0;
            ctx.stroke();
        });

        // Impact spark explosion
        if (strikeProgress < 0.1) {
            for (let i = 0; i < 3; i++) {
                d.sparks.push(new Spark(eff.tx, eff.ty, '#ffffff', 2));
            }
        }
    }

    // 3. Crawler Lightning (Back to the Future style overcharge)
    if (eff.t >= CONFIG.crawlTime && eff.t < CONFIG.shatterTime) {
        const crawlProgress = (eff.t - CONFIG.crawlTime) / (CONFIG.shatterTime - CONFIG.crawlTime);
        const intensity = 1 - Math.pow(crawlProgress, 3); // Fades out slowly

        // Throttle spawns to not melt canvas, but keep it very dense
        if (Math.random() < 0.6 * intensity) {
            // Randomly spawn 1-3 crawlers
            const numSpawns = Math.floor(1 + Math.random() * 2);
            for (let i = 0; i < numSpawns; i++) {
                d.crawlers.push(new CrawlLightning(eff.tx, eff.ty, 60)); // Avatar radius ~ 60
            }
        }
    }

    // Process & Draw Crawlers
    for (let i = d.crawlers.length - 1; i >= 0; i--) {
        d.crawlers[i].update(dt);
        d.crawlers[i].draw(ctx, 1);
        if (d.crawlers[i].life <= 0) {
            d.crawlers.splice(i, 1);
        }
    }

    // 4. Overcharge (Orbs)
    if (eff.t >= CONFIG.shatterTime) {
        const shatterProgress = (eff.t - CONFIG.shatterTime) / (CONFIG.duration - CONFIG.shatterTime);
        const orbAlpha = 1 - Math.pow(shatterProgress, 3);

        if (eff.t > CONFIG.duration - 500) {
            d.orbs.forEach(o => o.phase = 'absorb');
        }

        d.orbs.forEach(o => {
            o.update(eff.tx, eff.ty, dt);
            o.draw(ctx, orbAlpha);
        });

        // Residual sparks fading upward
        if (Math.random() < 0.2) {
            const s = new Spark(eff.tx + (Math.random() - 0.5) * 100, eff.ty + (Math.random() - 0.5) * 100, '#00ffff', 0.2);
            s.vy -= 2; // Float up
            d.sparks.push(s);
        }
    }

    // Process sparks
    ctx.globalCompositeOperation = 'lighter';
    for (let i = d.sparks.length - 1; i >= 0; i--) {
        d.sparks[i].update(0, 0.1);
        d.sparks[i].draw(ctx, 1);
        if (d.sparks[i].life <= 0) {
            d.sparks.splice(i, 1);
        }
    }

    ctx.restore();
    return true;
};
